import styled, { keyframes } from 'styled-components';
import { Link as LinkR } from 'react-router-dom';

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-8px); }
`;

/* ---------- page shell ---------- */
export const AboutPage = styled.main`
  color: #1f2d3d;
`;

/* ---------- hero band ---------- */
export const AboutHero = styled.section`
  position: relative;
  overflow: hidden;
  text-align: center;
  padding: 52px 18px 44px;
  background:
    radial-gradient(1000px 420px at 80% -20%, rgba(1, 191, 113, 0.16), transparent 60%),
    radial-gradient(800px 420px at 10% 120%, rgba(255, 154, 60, 0.14), transparent 55%),
    linear-gradient(180deg, #fffdf7 0%, #f4fbf4 100%);
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);

  @media screen and (min-width: 768px) {
    padding: 72px 24px 60px;
  }
`;

export const Eyebrow = styled.span`
  display: inline-block;
  background: rgba(1, 191, 113, 0.12);
  color: #0a8f57;
  font-weight: 700;
  font-size: var(--text-xs);
  letter-spacing: 0.05em;
  padding: 7px 15px;
  border-radius: 999px;
  margin-bottom: 18px;
`;

export const AboutTitle = styled.h1`
  margin: 0 0 14px;
  font-size: var(--text-3xl);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.05;

  span {
    background: linear-gradient(90deg, #01bf71, #ff9a3c);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

export const AboutLead = styled.p`
  margin: 0 auto;
  max-width: 40em;
  color: #46586a;
  font-size: var(--text-base);
  line-height: 1.6;
`;

export const HeroEmojis = styled.div`
  margin-top: 26px;
  display: flex;
  justify-content: center;
  gap: 14px;
  font-size: var(--text-2xl);

  span {
    display: inline-block;
    animation: ${float} 5s ease-in-out infinite;
  }
  span:nth-child(2) { animation-delay: 0.4s; }
  span:nth-child(3) { animation-delay: 0.8s; }
  span:nth-child(4) { animation-delay: 1.2s; }
  span:nth-child(5) { animation-delay: 1.6s; }
`;

/* ---------- stats strip ---------- */
export const StatsRow = styled.section`
  max-width: 1080px;
  margin: -34px auto 0;
  padding: 0 24px;
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media screen and (min-width: 760px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const Stat = styled.div`
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.07);
  border-radius: 16px;
  padding: 22px 18px;
  text-align: center;
  box-shadow: 0 14px 30px rgba(31, 45, 61, 0.08);
`;

export const StatNum = styled.div`
  font-size: var(--text-xl);
  font-weight: 800;
  color: #01bf71;
  line-height: 1;
`;

export const StatLabel = styled.div`
  margin-top: 8px;
  font-size: var(--text-sm);
  color: #6a7b8a;
  line-height: 1.35;
`;

/* ---------- generic section ---------- */
export const Section = styled.section`
  max-width: 1080px;
  margin: 0 auto;
  padding: 48px 18px 0;

  @media screen and (min-width: 768px) {
    padding: 64px 24px 0;
  }
`;

export const SectionHead = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

export const SectionTitle = styled.h2`
  margin: 0 0 8px;
  font-size: var(--text-2xl);
  font-weight: 800;
  letter-spacing: -0.01em;
`;

export const SectionSub = styled.p`
  margin: 0 auto;
  max-width: 34em;
  color: #6a7b8a;
  font-size: var(--text-base);
  line-height: 1.5;
`;

/* ---------- story cards ---------- */
export const StoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 22px;
  align-items: stretch;

  @media screen and (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StoryCard = styled.article`
  position: relative;
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.07);
  border-radius: 18px;
  padding: 30px 26px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 5px;
    background: linear-gradient(90deg, #01bf71, #ff9a3c);
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(31, 45, 61, 0.12);
  }
`;

export const StoryBadge = styled.span`
  position: absolute;
  top: 16px;
  right: 18px;
  font-size: var(--text-xs);
  font-weight: 800;
  color: rgba(1, 191, 113, 0.28);
`;

export const StoryIllustration = styled.img`
  width: 100%;
  max-width: 190px;
  height: 150px;
  object-fit: contain;
  margin: 6px 0 18px;
`;

export const StoryHeading = styled.h3`
  margin: 0 0 10px;
  font-size: var(--text-lg);
  font-weight: 700;
`;

export const StoryText = styled.p`
  margin: 0;
  color: #556575;
  font-size: var(--text-sm);
  line-height: 1.6;
`;

/* ---------- offer chips ---------- */
export const OfferGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;

  @media screen and (min-width: 760px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const OfferChip = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 14px;
  padding: 14px 16px;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: #fff;
    transform: translateY(-2px);
  }
`;

export const OfferEmoji = styled.span`
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  font-size: var(--text-xl);
  border-radius: 11px;
  background: rgba(1, 191, 113, 0.1);
`;

export const OfferLabel = styled.span`
  font-weight: 600;
  font-size: var(--text-sm);
  color: #1f2d3d;
`;

/* ---------- visit / contact ---------- */
export const VisitCard = styled.section`
  max-width: 1080px;
  margin: 48px 18px 56px;
  padding: 30px 26px;
  background: linear-gradient(135deg, #12313f 0%, #1f2d3d 100%);
  border-radius: 24px;
  color: #fff;
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;
  align-items: center;

  @media screen and (min-width: 860px) {
    grid-template-columns: 1.1fr 1fr;
    gap: 36px;
    margin: 64px auto 72px;
    padding: 40px;
  }
`;

export const VisitIntro = styled.div`
  h2 {
    margin: 0 0 10px;
    font-size: var(--text-2xl);
    font-weight: 800;
  }
  p {
    margin: 0 0 22px;
    color: rgba(255, 255, 255, 0.72);
    line-height: 1.6;
    max-width: 30em;
  }
`;

export const VisitCTA = styled(LinkR)`
  display: inline-block;
  background: #01bf71;
  color: #fff;
  font-weight: 700;
  text-decoration: none;
  padding: 14px 28px;
  border-radius: 999px;
  box-shadow: 0 10px 24px rgba(1, 191, 113, 0.35);
  transition: transform 0.15s ease, background 0.15s ease;

  &:hover {
    background: #04a763;
    transform: translateY(-2px);
  }
`;

export const VisitList = styled.div`
  display: grid;
  gap: 14px;
`;

export const VisitItem = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 14px 16px;
`;

export const VisitIcon = styled.span`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  font-size: var(--text-lg);
  border-radius: 10px;
  background: rgba(1, 191, 113, 0.18);
`;

export const VisitLabel = styled.div`
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.55);
`;

export const VisitValue = styled.div`
  font-size: var(--text-base);
  font-weight: 600;
  color: #fff;
`;
